import { ColumnDef } from "@tanstack/react-table";
import { getInitialColumnSizing } from "./getInitialColumnSizing";

type WeightedId = { id: string; weight: number };

// Distributes total across items by weight using largest-remainder so the sum matches exactly.
function distributeByProportionalWeights(
	items: WeightedId[],
	total: number,
): Record<string, number> {
	if (items.length === 0) return {};
	if (total <= 0) {
		return Object.fromEntries(items.map((it) => [it.id, 0]));
	}
	const wsum = items.reduce((a, it) => a + it.weight, 0);
	if (wsum <= 0) {
		// no weights — split evenly
		return distributeByProportionalWeights(
			items.map((it) => ({ id: it.id, weight: 1 })),
			total,
		);
	}
	const withParts = items.map((it) => {
		const exact = (it.weight / wsum) * total;
		const f = Math.floor(exact);
		return { id: it.id, floor: f, frac: exact - f };
	});
	const used = withParts.reduce((a, p) => a + p.floor, 0);
	const remainder = total - used;
	const order = [...withParts].sort((a, b) => b.frac - a.frac);
	const out: Record<string, number> = {};
	for (const p of withParts) {
		out[p.id] = p.floor;
	}
	for (let k = 0; k < remainder; k++) {
		const { id } = order[k]!;
		out[id] = (out[id] ?? 0) + 1;
	}
	return out;
}

// Sizes visible columns to fill availableWidth: fixed columns keep their default sizes, resizable columns share the remainder proportionally. Falls back to scaling all columns when fixed defaults exceed the container.
export function getProportionalColumnSizing<TData, TValue>(
	columnDefs: ColumnDef<TData, TValue>[],
	availableWidth: number,
): Record<string, number> {
	const base = getInitialColumnSizing(columnDefs, undefined);
	const ids = columnDefs.map((c) => c.id).filter((id): id is string => Boolean(id));
	if (ids.length === 0 || availableWidth <= 0) {
		return base;
	}

	const resizable = new Set(
		columnDefs.filter((c) => c.id && c.enableResizing === true).map((c) => c.id as string),
	);
	const flexIds = ids.filter((id) => resizable.has(id));
	const fixedIds = ids.filter((id) => !resizable.has(id));

	if (flexIds.length === 0) {
		return distributeByProportionalWeights(
			ids.map((id) => ({ id, weight: base[id] ?? 0 })),
			availableWidth,
		);
	}

	const sumFixed = fixedIds.reduce((a, id) => a + (base[id] ?? 0), 0);
	const flexBudget = availableWidth - sumFixed;

	if (flexBudget <= 0) {
		return distributeByProportionalWeights(
			ids.map((id) => ({ id, weight: base[id] ?? 0 })),
			availableWidth,
		);
	}

	const flexSizes = distributeByProportionalWeights(
		flexIds.map((id) => ({ id, weight: base[id] ?? 0 })),
		flexBudget,
	);
	const out: Record<string, number> = { ...flexSizes };
	for (const id of fixedIds) {
		out[id] = base[id] ?? 0;
	}
	return out;
}
